/**
 * FantasyPointsSummary
 *
 * Displays a user-facing breakdown of how fantasy points are calculated
 * for a given tournament. Reads configurable rules from the API so that
 * any change the admin makes is immediately reflected here.
 *
 * Usage:
 *   <FantasyPointsSummary tournamentId={tournament.id} />
 */

import React, { useEffect, useState } from 'react';
import { apiService } from '../services/api';

interface Band {
  min_sr?: number | null;
  max_sr?: number | null;
  min_economy?: number | null;
  max_economy?: number | null;
  points: number;
}

interface FpRules {
  duck_enabled: boolean;
  duck_points: number;
  duck_min_balls: number;
  sr_bonus_enabled: boolean;
  sr_bonus_min_balls: number;
  sr_bonus_bands: Band[];
  sr_penalty_enabled: boolean;
  sr_penalty_min_balls: number;
  sr_penalty_bands: Band[];
  economy_enabled: boolean;
  economy_min_overs: number;
  economy_bands: Band[];
  pom_enabled: boolean;
  pom_bonus_points: number;
  playing_xi_enabled: boolean;
  playing_xi_bonus_points: number;
  winning_team_enabled: boolean;
  winning_team_bonus_points: number;
}

interface Props {
  tournamentId: number;
  /** Optional class override for the outer container */
  className?: string;
}

// ─── helpers ─────────────────────────────────────────────────────────────────

function pts(n: number) {
  const s = n > 0 ? `+${n}` : `${n}`;
  return <span className={n >= 0 ? 'text-green-400 font-semibold' : 'text-red-400 font-semibold'}>{s}</span>;
}

function bandLabel(band: Band, type: 'sr' | 'economy'): string {
  if (type === 'sr') {
    const lo = band.min_sr;
    const hi = band.max_sr;
    if (lo !== null && lo !== undefined && hi !== null && hi !== undefined) return `SR > ${lo} to ≤ ${hi}`;
    if (lo !== null && lo !== undefined) return `SR > ${lo}`;
    if (hi !== null && hi !== undefined) return `SR ≤ ${hi}`;
    return 'Any SR';
  } else {
    const lo = band.min_economy;
    const hi = band.max_economy;
    if (lo !== null && lo !== undefined && hi !== null && hi !== undefined) return `Economy > ${lo} to ≤ ${hi}`;
    if (lo !== null && lo !== undefined) return `Economy > ${lo}`;
    if (hi !== null && hi !== undefined) return `Economy ≤ ${hi}`;
    return 'Any Economy';
  }
}

// ─── component ───────────────────────────────────────────────────────────────

const FantasyPointsSummary: React.FC<Props> = ({ tournamentId, className = '' }) => {
  const [rules, setRules] = useState<FpRules | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tournamentId) return;
    setLoading(true);
    apiService.getFantasyPointRulesPublic(tournamentId)
      .then((res: any) => { if (res?.rules) setRules(res.rules); })
      .catch((err: any) => setError(err?.message || 'Could not load scoring rules'))
      .finally(() => setLoading(false));
  }, [tournamentId]);

  if (loading) {
    return (
      <div className={`flex items-center gap-2 text-gray-400 text-sm py-4 ${className}`}>
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-400"></div>
        Loading points breakdown…
      </div>
    );
  }

  if (error || !rules) {
    return <div className={`text-gray-400 text-sm ${className}`}>Points breakdown unavailable.</div>;
  }

  const srBonusBands   = (rules.sr_bonus_bands   || []).filter(b => b.points !== 0);
  const srPenaltyBands = rules.sr_penalty_bands   || [];
  const econBands      = (rules.economy_bands     || []).filter(b => b.points !== 0);

  return (
    <div className={`space-y-5 ${className}`}>
      <h3 className="text-base font-semibold text-white">How Fantasy Points are Calculated</h3>

      {/* ── Base Points (full breakdown) ── */}
      <div className="rounded-lg bg-blue-500/10 border border-blue-500/20 p-4 text-sm text-blue-200">
        <p className="font-medium text-white mb-3">Base Points (Scoring Rules)</p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Batting */}
          <div>
            <p className="text-xs font-semibold text-blue-300 uppercase tracking-wide mb-2">Batting</p>
            <div className="space-y-1">
              {[
                ['Run scored', '+1 each'],
                ['Four (boundary bonus)', '+2 each'],
                ['Six (boundary bonus)', '+3 each'],
                ['30+ runs in an innings', '+5'],
                ['Half century (50+)', '+15'],
                ['75+ runs in an innings', '+25'],
                ['Century (100+)', '+50'],
              ].map(([label, val]) => (
                <div key={label} className="flex justify-between gap-2">
                  <span className="text-blue-200/80">{label}</span>
                  <span className="text-green-400 font-semibold whitespace-nowrap">{val}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Bowling */}
          <div>
            <p className="text-xs font-semibold text-blue-300 uppercase tracking-wide mb-2">Bowling</p>
            <div className="space-y-1">
              {[
                ['Wicket taken', '+30 each'],
                ['Maiden over', '+5 each'],
                ['Wide bowled', '−1 each'],
                ['No ball bowled', '−1 each'],
                ['3-wicket haul bonus', '+10'],
                ['4-wicket haul bonus', '+15'],
                ['5+ wicket haul bonus', '+20'],
              ].map(([label, val]) => (
                <div key={label} className="flex justify-between gap-2">
                  <span className="text-blue-200/80">{label}</span>
                  <span className={val.startsWith('−') ? 'text-red-400 font-semibold whitespace-nowrap' : 'text-green-400 font-semibold whitespace-nowrap'}>{val}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Fielding */}
          <div>
            <p className="text-xs font-semibold text-blue-300 uppercase tracking-wide mb-2">Fielding &amp; Dismissals</p>
            <div className="space-y-1">
              {[
                ['Catch', '+10 each'],
                ['3+ catches in a match', '+10 bonus'],
                ['Run out', '+10 each'],
                ['Stumping', '+10 each'],
                ['LBW (bowler bonus)', '+2 each'],
                ['Bowled (bowler bonus)', '+5 each'],
                ['Catch &amp; Bowled', '+5 each'],
              ].map(([label, val]) => (
                <div key={label} className="flex justify-between gap-2">
                  <span className="text-blue-200/80" dangerouslySetInnerHTML={{ __html: label }} />
                  <span className="text-green-400 font-semibold whitespace-nowrap">{val}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Bonus / Penalty cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

        {/* Playing XI */}
        {rules.playing_xi_enabled && (
          <div className="rounded-lg bg-white/5 border border-white/10 p-4">
            <p className="font-medium text-white text-sm mb-1">Playing XI Appearance</p>
            <p className="text-gray-400 text-xs mb-2">Awarded to every player who participates in the match.</p>
            <p className="text-sm">{pts(rules.playing_xi_bonus_points)} per player</p>
          </div>
        )}

        {/* Winning team */}
        {rules.winning_team_enabled && (
          <div className="rounded-lg bg-white/5 border border-white/10 p-4">
            <p className="font-medium text-white text-sm mb-1">Winning Team</p>
            <p className="text-gray-400 text-xs mb-2">Awarded to all players from the winning side.</p>
            <p className="text-sm">{pts(rules.winning_team_bonus_points)} per player</p>
          </div>
        )}

        {/* POM */}
        {rules.pom_enabled && (
          <div className="rounded-lg bg-white/5 border border-white/10 p-4">
            <p className="font-medium text-white text-sm mb-1">Player of the Match</p>
            <p className="text-gray-400 text-xs mb-2">Awarded to the official Player of the Match.</p>
            <p className="text-sm">{pts(rules.pom_bonus_points)}</p>
          </div>
        )}

        {/* Duck Out */}
        {rules.duck_enabled && (
          <div className="rounded-lg bg-white/5 border border-white/10 p-4">
            <p className="font-medium text-white text-sm mb-1">Duck Out</p>
            <p className="text-gray-400 text-xs mb-2">
              Any Batsman, WK, or All-Rounder dismissed for 0 (no minimum balls requirement).
            </p>
            <p className="text-sm">{pts(rules.duck_points)}</p>
          </div>
        )}
      </div>

      {/* ── SR Bonus ── */}
      {rules.sr_bonus_enabled && srBonusBands.length > 0 && (
        <div className="rounded-lg bg-white/5 border border-white/10 p-4">
          <p className="font-medium text-white text-sm mb-1">
            Strike Rate Bonus <span className="text-gray-400 font-normal">(all roles, min {rules.sr_bonus_min_balls} balls faced)</span>
          </p>
          <div className="mt-2 divide-y divide-white/10">
            {srBonusBands.map((band, i) => (
              <div key={i} className="flex justify-between py-1.5 text-sm">
                <span className="text-gray-300">{bandLabel(band, 'sr')}</span>
                <span>{pts(band.points)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── SR Penalty ── */}
      {rules.sr_penalty_enabled && srPenaltyBands.length > 0 && (
        <div className="rounded-lg bg-white/5 border border-white/10 p-4">
          <p className="font-medium text-white text-sm mb-1">
            Strike Rate Penalty <span className="text-gray-400 font-normal">(Batsman / WK / AR, min {rules.sr_penalty_min_balls} balls)</span>
          </p>
          <div className="mt-2 divide-y divide-white/10">
            {srPenaltyBands.map((band, i) => (
              <div key={i} className="flex justify-between py-1.5 text-sm">
                <span className="text-gray-300">{bandLabel(band, 'sr')}</span>
                <span>{pts(band.points)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Economy Rate ── */}
      {rules.economy_enabled && econBands.length > 0 && (
        <div className="rounded-lg bg-white/5 border border-white/10 p-4">
          <p className="font-medium text-white text-sm mb-1">
            Economy Rate <span className="text-gray-400 font-normal">(min {rules.economy_min_overs} over{rules.economy_min_overs !== 1 ? 's' : ''} bowled)</span>
          </p>
          <div className="mt-2 divide-y divide-white/10">
            {econBands.map((band, i) => (
              <div key={i} className="flex justify-between py-1.5 text-sm">
                <span className="text-gray-300">{bandLabel(band, 'economy')}</span>
                <span>{pts(band.points)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Captain / VC note */}
      <div className="rounded-lg bg-white/5 border border-white/10 p-4 text-sm text-gray-300">
        <p className="font-medium text-white mb-1">Captain &amp; Vice-Captain Multipliers</p>
        <p className="text-gray-400 text-xs">Captain: 2× total points &nbsp;·&nbsp; Vice-Captain: 1.5× total points</p>
      </div>
    </div>
  );
};

export default FantasyPointsSummary;
